/** Admin manager for the Spotlight rotating hero gallery.
 * Upload real honoree photos (or paste URLs), caption, reorder, delete. */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, ArrowUp, ArrowDown, Upload, Image as ImageIcon } from "lucide-react";

const MAX_MB = 4;

const AddImageDialog = ({ open, onOpenChange, onSaved }) => {
  const [label, setLabel] = useState("");
  const [alt, setAlt] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setLabel(""); setAlt(""); setUrl(""); setFile(null); setPreview("");
    }
  }, [open]);

  const pickFile = (f) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Please choose a photo under ${MAX_MB}MB.`);
      return;
    }
    setFile(f);
    setUrl("");
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!label.trim()) {
      toast.error("Please add a short caption (shown under the image).");
      return;
    }
    if (!file && !url.trim()) {
      toast.error("Upload a photo or paste an image URL.");
      return;
    }
    setSaving(true);
    try {
      const payload = { label: label.trim(), alt: alt.trim() || label.trim() };
      if (file && preview) {
        payload.image_base64 = preview.split(",")[1];
        payload.content_type = file.type || "image/jpeg";
      } else {
        payload.url = url.trim();
      }
      await api.post("/spotlight-hero-images", payload);
      toast.success("Image added to the gallery.");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add the image.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[#FDFBF5] border-[#EBE3D2]" data-testid="hero-image-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#2A1810]">Add Hero Image</DialogTitle>
          <DialogDescription className="text-[#6B5944]">
            Upload a photo of an honoree, or paste an image link.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Upload zone */}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full border-2 border-dashed border-[#C28A2E]/40 rounded-xl p-5 text-center hover:border-[#C28A2E] transition-colors"
            data-testid="hero-upload-zone"
          >
            {preview ? (
              <img src={preview} alt="Preview" className="mx-auto max-h-40 rounded-md object-cover" data-testid="hero-upload-preview" />
            ) : (
              <div className="text-[#6B5944] text-sm flex flex-col items-center gap-2">
                <Upload size={20} className="text-[#C28A2E]" />
                Click to upload a photo (JPG/PNG, up to {MAX_MB}MB)
              </div>
            )}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])}
            data-testid="hero-file-input"
          />
          <div className="flex items-center gap-3 text-xs text-[#6B5944]">
            <div className="h-px flex-1 bg-[#EBE3D2]" /> or <div className="h-px flex-1 bg-[#EBE3D2]" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#6B5944] mb-1.5">Image URL</div>
            <Input
              value={url}
              onChange={(e) => { setUrl(e.target.value); setFile(null); setPreview(""); }}
              placeholder="https://…"
              data-testid="hero-url-input"
            />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#6B5944] mb-1.5">Caption * (shown under the image)</div>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Compassion in a moment of need" data-testid="hero-label-input" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#6B5944] mb-1.5">Alt text (accessibility, optional)</div>
            <Input value={alt} onChange={(e) => setAlt(e.target.value)} placeholder="Describe the moment" data-testid="hero-alt-input" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="hero-image-save">
            {saving ? "Adding…" : "Add to gallery"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const HeroImagesManager = () => {
  const [images, setImages] = useState([]);
  const [addOpen, setAddOpen] = useState(false);

  const load = () => {
    api.get("/public/spotlight-hero-images").then((r) => setImages(r.data.images || [])).catch(() => {});
  };
  useEffect(load, []);

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    try {
      await Promise.all([
        api.put(`/spotlight-hero-images/${images[i].id}`, { order: images[j].order }),
        api.put(`/spotlight-hero-images/${images[j].id}`, { order: images[i].order }),
      ]);
      load();
    } catch {
      toast.error("Reorder failed.");
    }
  };

  const rename = async (img) => {
    const next = window.prompt("Caption for this image:", img.label);
    if (next === null || next.trim() === "" || next === img.label) return;
    try {
      await api.put(`/spotlight-hero-images/${img.id}`, { label: next.trim() });
      load();
    } catch {
      toast.error("Update failed.");
    }
  };

  const remove = async (img) => {
    if (!window.confirm(`Remove "${img.label}" from the hero gallery?`)) return;
    try {
      await api.delete(`/spotlight-hero-images/${img.id}`);
      toast.success("Image removed.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="space-y-4" data-testid="hero-images-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#6B5944] max-w-xl">
          These images rotate in the hero of the public Spotlight page. Replace the placeholders
          with real honoree photographs as CCS Spotlight grows.
        </p>
        <Button onClick={() => setAddOpen(true)} className="bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="add-hero-image-button">
          <Plus size={16} className="mr-1.5" /> Add Image
        </Button>
      </div>

      {images.length === 0 ? (
        <div className="py-16 text-center text-[#6B5944] border border-dashed border-[#EBE3D2] rounded-2xl">
          <ImageIcon size={22} className="mx-auto mb-2 text-[#C28A2E]" />
          No hero images yet — add your first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {images.map((img, i) => (
            <div key={img.id} className="bg-white border border-[#EBE3D2] rounded-xl overflow-hidden" data-testid={`hero-image-row-${img.id}`}>
              <img src={img.src} alt={img.alt} className="w-full aspect-[4/3] object-cover" />
              <div className="p-3">
                <button
                  onClick={() => rename(img)}
                  className="text-left text-sm font-medium text-[#2A1810] hover:text-[#C28A2E] transition-colors"
                  title="Click to edit caption"
                  data-testid={`hero-caption-${img.id}`}
                >
                  ✦ {img.label}
                </button>
                <div className="mt-2 flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`hero-up-${img.id}`}>
                      <ArrowUp size={13} />
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => move(i, 1)} disabled={i === images.length - 1} data-testid={`hero-down-${img.id}`}>
                      <ArrowDown size={13} />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-[#6B5944]">{img.has_upload ? "Uploaded" : "Linked"}</span>
                    <Button variant="outline" size="sm" className="h-7 px-2 text-red-600 hover:text-red-700" onClick={() => remove(img)} data-testid={`hero-delete-${img.id}`}>
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddImageDialog open={addOpen} onOpenChange={setAddOpen} onSaved={load} />
    </div>
  );
};

export default HeroImagesManager;
